/**
 * VincularComissoesRecebidas - Registrar comissões recebidas do banco
 * Permite vincular contratos pagos, ajustar valores e gerar relatórios
 */
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { 
  ArrowLeft, Building, DollarSign, Calendar, FileSpreadsheet, 
  ChevronDown, ChevronRight, AlertTriangle, Download, Upload,
  Calculator, Check, X, Save, Landmark, User, RefreshCw, Filter,
  Edit2, Percent, CheckCircle2, XCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Função para formatar moeda
const formatCurrency = (value) => {
  return `R$ ${parseFloat(value || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`;
};

export default function VincularComissoesRecebidas() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [contratos, setContratos] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [correspondentes, setCorrespondentes] = useState([]);
  const [tabelasComissao, setTabelasComissao] = useState([]);
  
  // Filtros
  const [filtroBanco, setFiltroBanco] = useState("todos");
  const [filtroCorrespondente, setFiltroCorrespondente] = useState("todos");
  const [filtroStatus, setFiltroStatus] = useState("pendentes"); // pendentes, pagos, todos
  
  // Estados de edição
  const [contratosSelecionados, setContratosSelecionados] = useState([]);
  const [modoEdicao, setModoEdicao] = useState({});
  const [valoresEditados, setValoresEditados] = useState({});
  
  // Dialog de importação
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [arquivoImportado, setArquivoImportado] = useState(null);
  const [dadosImportados, setDadosImportados] = useState([]);

  // Carregar dados
  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    setLoading(true);
    try {
      const [contratosRes, bancosRes, correspRes, tabelasRes] = await Promise.all([
        axios.get(`${API}/contratos`).catch(() => ({ data: { contratos: [] } })),
        axios.get(`${API}/bancos`).catch(() => ({ data: [] })),
        axios.get(`${API}/correspondentes`).catch(() => ({ data: { correspondentes: [] } })),
        axios.get(`${API}/tabelas-comissao`).catch(() => ({ data: { tabelas: [] } }))
      ]);
      
      setContratos(contratosRes.data?.contratos || contratosRes.data || []);
      setBancos(bancosRes.data || []);
      setCorrespondentes(correspRes.data?.correspondentes || []);
      setTabelasComissao(tabelasRes.data?.tabelas || tabelasRes.data || []);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  // Função para buscar comissão da tabela
  const buscarComissaoDaTabela = (contrato) => {
    // Primeiro tenta encontrar no próprio contrato (se já tiver comissões cadastradas)
    if (contrato.comissoes && contrato.comissoes.length > 0) {
      const comissao = contrato.comissoes[0];
      return {
        empresa_percentual: parseFloat(comissao.empresa_percentual || comissao.empresa || 0),
        tele_venda_percentual: parseFloat(comissao.tele_venda_percentual || comissao.tele_venda || 0),
        corretor_percentual: parseFloat(comissao.corretor_percentual || comissao.corretor || 0),
        atendente_percentual: parseFloat(comissao.atendente_percentual || comissao.atendente || 0),
        gerente_adm_percentual: parseFloat(comissao.gerente_adm_percentual || comissao.gerente_adm || 0),
        gerente_oper_percentual: parseFloat(comissao.gerente_oper_percentual || comissao.gerente_oper || 0)
      };
    }

    // Busca na tabela de comissão do banco
    const bancoContrato = contrato.banco || contrato.nome_banco || contrato.banco_nome;
    const tipoOp = contrato.tipo_operacao || contrato.produto;
    
    // Procura o banco que tem tabelas de comissão
    const banco = bancos.find(b => 
      b.nome === bancoContrato || 
      b.codigo === contrato.banco_codigo
    );
    
    if (banco && banco.tabelas_comissao) {
      // Busca a tabela que corresponde ao tipo de operação
      const tabela = banco.tabelas_comissao.find(t => 
        (t.tipo_operacao && t.tipo_operacao.toLowerCase().includes(tipoOp?.toLowerCase() || '')) ||
        (t.nome_tabela && t.nome_tabela.toLowerCase().includes(tipoOp?.toLowerCase() || ''))
      ) || banco.tabelas_comissao[0];
      
      if (tabela && tabela.itens && tabela.itens.length > 0) {
        const item = tabela.itens[0];
        return {
          empresa_percentual: parseFloat(item.empresa_percentual || 3),
          tele_venda_percentual: parseFloat(item.tele_venda_percentual || 0),
          corretor_percentual: parseFloat(item.corretor_percentual || 30),
          atendente_percentual: parseFloat(item.atendente_percentual || 5),
          gerente_adm_percentual: parseFloat(item.gerente_adm_percentual || 10),
          gerente_oper_percentual: parseFloat(item.gerente_oper_percentual || 0)
        };
      }
    }

    // Busca nas tabelas de comissão globais
    const tabelasArray = Array.isArray(tabelasComissao) ? tabelasComissao : [];
    const tabelaGlobal = tabelasArray.find(t => 
      (t.banco_nome === bancoContrato || t.banco_codigo === contrato.banco_codigo) &&
      (!tipoOp || (t.tipo_operacao && t.tipo_operacao.toLowerCase().includes(tipoOp.toLowerCase())))
    );

    if (tabelaGlobal && tabelaGlobal.itens && tabelaGlobal.itens.length > 0) {
      const item = tabelaGlobal.itens[0];
      return {
        empresa_percentual: parseFloat(item.empresa_percentual || 3),
        tele_venda_percentual: parseFloat(item.tele_venda_percentual || 0),
        corretor_percentual: parseFloat(item.corretor_percentual || 30),
        atendente_percentual: parseFloat(item.atendente_percentual || 5),
        gerente_adm_percentual: parseFloat(item.gerente_adm_percentual || 10),
        gerente_oper_percentual: parseFloat(item.gerente_oper_percentual || 0)
      };
    }
    
    // Valores padrão se não encontrar tabela
    return {
      empresa_percentual: parseFloat(contrato.comissao_calculada || contrato.taxa_comissao || 3),
      tele_venda_percentual: 0,
      corretor_percentual: 30,
      atendente_percentual: 5,
      gerente_adm_percentual: 10,
      gerente_oper_percentual: 0
    };
  };

  // Filtrar contratos
  const contratosFiltrados = useMemo(() => {
    return contratos.filter(c => {
      const valor = parseFloat(c.valor_liberado || c.valor_solicitado || 0);
      if (valor <= 0) return false;
      
      // Filtro por banco
      if (filtroBanco !== "todos") {
        const banco = c.banco || c.nome_banco || "";
        if (banco !== filtroBanco) return false;
      }
      
      // Filtro por correspondente
      if (filtroCorrespondente !== "todos") {
        const corresp = c.correspondente || "";
        if (corresp !== filtroCorrespondente) return false;
      }
      
      // Filtro por status de pagamento
      const comissaoPaga = c.comissao_paga || c.comissao_recebida || false;
      if (filtroStatus === "pendentes" && comissaoPaga) return false;
      if (filtroStatus === "pagos" && !comissaoPaga) return false;
      
      return true;
    }).map(c => {
      const valorLiberado = parseFloat(c.valor_liberado || c.valor_solicitado || 0);
      const comissaoTabela = buscarComissaoDaTabela(c);
      const empresaPerc = comissaoTabela.empresa_percentual;
      const comissaoEmpresaValor = valorLiberado * (empresaPerc / 100);
      
      return {
        ...c,
        valor_liberado_num: valorLiberado,
        comissao_esperada: comissaoEmpresaValor,
        percentual_esperado: empresaPerc,
        // Percentuais da tabela (esperados)
        tele_percentual_tabela: comissaoTabela.tele_venda_percentual,
        corretor_percentual_tabela: comissaoTabela.corretor_percentual,
        atendente_percentual_tabela: comissaoTabela.atendente_percentual,
        ger_adm_percentual_tabela: comissaoTabela.gerente_adm_percentual,
        ger_oper_percentual_tabela: comissaoTabela.gerente_oper_percentual,
        // Valores calculados da tabela (esperados)
        tele_valor_tabela: comissaoEmpresaValor * (comissaoTabela.tele_venda_percentual / 100),
        corretor_valor_tabela: comissaoEmpresaValor * (comissaoTabela.corretor_percentual / 100),
        atendente_valor_tabela: comissaoEmpresaValor * (comissaoTabela.atendente_percentual / 100),
        ger_adm_valor_tabela: comissaoEmpresaValor * (comissaoTabela.gerente_adm_percentual / 100),
        ger_oper_valor_tabela: comissaoEmpresaValor * (comissaoTabela.gerente_oper_percentual / 100)
      };
    });
  }, [contratos, filtroBanco, filtroCorrespondente, filtroStatus, bancos, tabelasComissao]);

  // Bancos e correspondentes disponíveis nos contratos
  const bancosDisponiveis = useMemo(() => {
    const set = new Set();
    contratos.forEach(c => {
      const banco = c.banco || c.nome_banco;
      if (banco) set.add(banco);
    });
    return Array.from(set).sort();
  }, [contratos]);

  const correspondentesDisponiveis = useMemo(() => {
    const set = new Set();
    contratos.forEach(c => {
      const corresp = c.correspondente;
      if (corresp) set.add(corresp);
    });
    return Array.from(set).sort();
  }, [contratos]);

  // Totais
  const totais = useMemo(() => {
    const selecionados = contratosFiltrados.filter(c => contratosSelecionados.includes(c.id));
    return {
      qtdTotal: contratosFiltrados.length,
      qtdSelecionados: selecionados.length,
      valorTotal: contratosFiltrados.reduce((acc, c) => acc + c.valor_liberado_num, 0),
      valorSelecionados: selecionados.reduce((acc, c) => acc + c.valor_liberado_num, 0),
      comissaoTotal: contratosFiltrados.reduce((acc, c) => acc + c.comissao_esperada, 0),
      comissaoSelecionados: selecionados.reduce((acc, c) => acc + c.comissao_esperada, 0)
    };
  }, [contratosFiltrados, contratosSelecionados]);

  // Toggle seleção de contrato
  const toggleSelecao = (contratoId) => {
    setContratosSelecionados(prev => 
      prev.includes(contratoId) 
        ? prev.filter(id => id !== contratoId)
        : [...prev, contratoId]
    );
  };

  // Selecionar/Deselecionar todos
  const toggleTodos = () => {
    if (contratosSelecionados.length === contratosFiltrados.length) {
      setContratosSelecionados([]);
    } else {
      setContratosSelecionados(contratosFiltrados.map(c => c.id));
    }
  };

  // Iniciar edição de comissão
  const iniciarEdicao = (contrato) => {
    setModoEdicao(prev => ({ ...prev, [contrato.id]: true }));
    setValoresEditados(prev => ({
      ...prev,
      [contrato.id]: {
        comissao_recebida_valor: contrato.comissao_recebida_valor ?? "",
        comissao_recebida_percentual: contrato.comissao_recebida_percentual ?? contrato.percentual_esperado ?? "",
        empresa_valor: contrato.empresa_valor ?? "",
        empresa_percentual: contrato.empresa_percentual ?? contrato.percentual_esperado ?? "",
        // Usar valores da tabela como padrão (usar ?? para permitir valor 0)
        tele_venda_percentual: contrato.tele_venda_percentual ?? contrato.tele_percentual_tabela ?? 0,
        tele_venda_valor: contrato.tele_venda_valor ?? contrato.tele_valor_tabela ?? 0,
        corretor_percentual: contrato.corretor_percentual ?? contrato.corretor_percentual_tabela ?? 30,
        corretor_valor: contrato.corretor_valor ?? contrato.corretor_valor_tabela ?? 0,
        atendente_percentual: contrato.atendente_percentual ?? contrato.atendente_percentual_tabela ?? 5,
        atendente_valor: contrato.atendente_valor ?? contrato.atendente_valor_tabela ?? 0,
        ger_administrativo_percentual: contrato.ger_administrativo_percentual ?? contrato.ger_adm_percentual_tabela ?? 10,
        ger_administrativo_valor: contrato.ger_administrativo_valor ?? contrato.ger_adm_valor_tabela ?? 0,
        ger_operacional_percentual: contrato.ger_operacional_percentual ?? contrato.ger_oper_percentual_tabela ?? 0,
        ger_operacional_valor: contrato.ger_operacional_valor ?? contrato.ger_oper_valor_tabela ?? 0
      }
    }));
  };

  // Cancelar edição
  const cancelarEdicao = (contratoId) => {
    setModoEdicao(prev => ({ ...prev, [contratoId]: false }));
    setValoresEditados(prev => {
      const novo = { ...prev };
      delete novo[contratoId];
      return novo;
    });
  };

  // Atualizar valor editado
  const atualizarValorEditado = (contratoId, campo, valor) => {
    setValoresEditados(prev => ({
      ...prev,
      [contratoId]: {
        ...prev[contratoId],
        [campo]: valor
      }
    }));
  };

  // Calcular valor a partir do percentual
  const calcularValorDePercentual = (contratoId, campo) => {
    const contrato = contratosFiltrados.find(c => c.id === contratoId);
    if (!contrato) return;
    
    const valores = valoresEditados[contratoId];
    const campoPercentual = campo + "_percentual";
    const campoValor = campo + "_valor";
    
    const percentual = parseFloat(valores[campoPercentual] || 0);
    const valorCalculado = (contrato.valor_liberado_num * percentual) / 100;
    
    atualizarValorEditado(contratoId, campoValor, valorCalculado.toFixed(2));
  };

  // Calcular percentual a partir do valor
  const calcularPercentualDeValor = (contratoId, campo) => {
    const contrato = contratosFiltrados.find(c => c.id === contratoId);
    if (!contrato) return;
    
    const valores = valoresEditados[contratoId];
    const campoPercentual = campo + "_percentual";
    const campoValor = campo + "_valor";
    
    const valor = parseFloat(valores[campoValor] || 0);
    const percentualCalculado = contrato.valor_liberado_num > 0 
      ? (valor / contrato.valor_liberado_num) * 100 
      : 0;
    
    atualizarValorEditado(contratoId, campoPercentual, percentualCalculado.toFixed(2));
  };

  // Salvar comissão recebida
  const salvarComissao = async (contratoId) => {
    const valores = valoresEditados[contratoId];
    if (!valores) return;
    
    try {
      const payload = {
        comissao_paga: true,
        comissao_recebida_valor: parseFloat(valores.comissao_recebida_valor || 0),
        comissao_recebida_percentual: parseFloat(valores.comissao_recebida_percentual || 0),
        empresa_valor: parseFloat(valores.empresa_valor || 0),
        empresa_percentual: parseFloat(valores.empresa_percentual || 0),
        atendente_valor: parseFloat(valores.atendente_valor || 0),
        atendente_percentual: parseFloat(valores.atendente_percentual || 0),
        corretor_valor: parseFloat(valores.corretor_valor || 0),
        corretor_percentual: parseFloat(valores.corretor_percentual || 0),
        tele_venda_valor: parseFloat(valores.tele_venda_valor || 0),
        tele_venda_percentual: parseFloat(valores.tele_venda_percentual || 0),
        ger_operacional_valor: parseFloat(valores.ger_operacional_valor || 0),
        ger_operacional_percentual: parseFloat(valores.ger_operacional_percentual || 0),
        ger_administrativo_valor: parseFloat(valores.ger_administrativo_valor || 0),
        ger_administrativo_percentual: parseFloat(valores.ger_administrativo_percentual || 0)
      };

      await axios.patch(`${API}/contratos/${contratoId}/comissao`, payload);
      
      // Atualiza estado local
      setContratos(prev => prev.map(c => {
        if (c.id === contratoId) {
          return {
            ...c,
            comissao_paga: true,
            comissao_recebida: true,
            ...payload,
            comissao_paga_em: new Date().toISOString()
          };
        }
        return c;
      }));
      
      cancelarEdicao(contratoId);
      toast.success("Comissão registrada com sucesso!");
    } catch (error) {
      console.error("Erro ao salvar comissão:", error);
      toast.error("Erro ao salvar comissão");
    }
  };

  // Marcar selecionados como pagos
  const marcarSelecionadosComoPagos = async () => {
    if (contratosSelecionados.length === 0) {
      toast.error("Selecione pelo menos um contrato");
      return;
    }
    
    try {
      // Preparar dados para cada contrato selecionado
      const contratosParaAtualizar = contratosFiltrados.filter(c => contratosSelecionados.includes(c.id));
      
      // Chamar endpoint em lote via query params
      const queryParams = contratosSelecionados.map(id => `contrato_ids=${id}`).join('&');
      await axios.patch(`${API}/contratos/comissao/batch?${queryParams}&comissao_paga=true`);
      
      // Atualizar estado local
      setContratos(prev => prev.map(c => {
        if (contratosSelecionados.includes(c.id)) {
          const contratoFiltrado = contratosParaAtualizar.find(cf => cf.id === c.id);
          return {
            ...c,
            comissao_paga: true,
            comissao_recebida: true,
            comissao_recebida_valor: contratoFiltrado?.comissao_esperada || parseFloat(c.valor_liberado || c.valor_solicitado || 0) * 0.03,
            comissao_recebida_percentual: contratoFiltrado?.percentual_esperado || 3,
            comissao_paga_em: new Date().toISOString()
          };
        }
        return c;
      }));
      
      toast.success(`${contratosSelecionados.length} contrato(s) marcado(s) como pago(s)`);
      setContratosSelecionados([]);
    } catch (error) {
      console.error("Erro ao marcar contratos como pagos:", error);
      toast.error("Erro ao marcar contratos como pagos");
    }
  };

  // Importar arquivo de comissões
  const importarArquivo = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setArquivoImportado(file);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        
        setDadosImportados(jsonData);
        toast.success(`${jsonData.length} registros encontrados no arquivo`);
      } catch (error) {
        toast.error("Erro ao ler arquivo");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // Vincular dados importados aos contratos
  const vincularDadosImportados = async () => {
    let vinculados = 0;
    let erros = 0;
    
    for (const c of contratos) {
      // Tentar encontrar o contrato nos dados importados
      const dadoImportado = dadosImportados.find(d => {
        const numContrato = d["Número do Contrato"] || d["Contrato"] || d["CONTRATO"] || "";
        return numContrato.toString() === (c.numero_contrato || c.codigo || "").toString();
      });
      
      if (dadoImportado) {
        const valorRecebido = parseFloat(
          dadoImportado["Comissão Recebida"] || 
          dadoImportado["Valor Comissão"] || 
          dadoImportado["COMISSÃO"] || 
          0
        );
        
        const valorLiberado = parseFloat(c.valor_liberado || c.valor_solicitado || 1);
        
        try {
          await axios.patch(`${API}/contratos/${c.id}/comissao`, {
            comissao_paga: true,
            comissao_recebida_valor: valorRecebido,
            comissao_recebida_percentual: valorLiberado > 0 ? (valorRecebido / valorLiberado) * 100 : 0,
            comissao_paga_em: dadoImportado["Data Pagamento"] || new Date().toISOString()
          });
          vinculados++;
        } catch (err) {
          console.error(`Erro ao vincular contrato ${c.numero_contrato}:`, err);
          erros++;
        }
      }
    }
    
    // Recarregar dados
    await carregarDados();
    
    if (erros > 0) {
      toast.warning(`${vinculados} contrato(s) vinculado(s), ${erros} erro(s)`);
    } else {
      toast.success(`${vinculados} contrato(s) vinculado(s) com sucesso!`);
    }
    
    setShowImportDialog(false);
    setDadosImportados([]);
    setArquivoImportado(null);
  };

  // Exportar relatório
  const exportarRelatorio = () => {
    const dados = contratosFiltrados.map(c => ({
      "Data Contrato": c.data_contrato ? new Date(c.data_contrato).toLocaleDateString("pt-BR") : "-",
      "Número Contrato": c.numero_contrato || c.codigo || "-",
      "Cliente": c.cliente_nome || c.cliente || "-",
      "Banco": c.banco || c.nome_banco || "-",
      "Correspondente": c.correspondente || "Geral",
      "Valor Liberado": c.valor_liberado_num,
      "% Esperado": c.percentual_esperado,
      "Comissão Esperada": c.comissao_esperada,
      "% Recebido": c.comissao_recebida_percentual || "",
      "Comissão Recebida": c.comissao_recebida_valor || "",
      "Status": c.comissao_paga ? "PAGO" : "PENDENTE",
      "Data Pagamento": c.data_pagamento_comissao ? new Date(c.data_pagamento_comissao).toLocaleDateString("pt-BR") : ""
    }));
    
    const ws = XLSX.utils.json_to_sheet(dados);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Comissões");
    
    XLSX.writeFile(wb, `vincular_comissoes_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success("Relatório exportado!");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vincular-comissoes">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-emerald-800">Vincular Comissões Recebidas</h1>
            <p className="text-sm text-slate-500">Registre os pagamentos de comissão recebidos do banco</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={carregarDados} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Atualizar
          </Button>
          <Button variant="outline" onClick={() => setShowImportDialog(true)} className="gap-2 text-blue-600">
            <Upload className="h-4 w-4" />
            Importar Pagamentos
          </Button>
          <Button variant="outline" onClick={exportarRelatorio} className="gap-2 text-green-600">
            <Download className="h-4 w-4" />
            Exportar
          </Button>
        </div>
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <FileSpreadsheet className="h-8 w-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold text-blue-800">{totais.qtdTotal}</p>
                <p className="text-sm text-blue-600">Contratos Filtrados</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <DollarSign className="h-8 w-8 text-green-600" />
              <div>
                <p className="text-xl font-bold text-green-800">{formatCurrency(totais.valorTotal)}</p>
                <p className="text-sm text-green-600">Valor Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Percent className="h-8 w-8 text-emerald-600" />
              <div>
                <p className="text-xl font-bold text-emerald-800">{formatCurrency(totais.comissaoTotal)}</p>
                <p className="text-sm text-emerald-600">Comissão Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={`bg-gradient-to-br ${totais.qtdSelecionados > 0 ? 'from-orange-50 to-orange-100 border-orange-200' : 'from-slate-50 to-slate-100 border-slate-200'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 className={`h-8 w-8 ${totais.qtdSelecionados > 0 ? 'text-orange-600' : 'text-slate-400'}`} />
              <div>
                <p className={`text-2xl font-bold ${totais.qtdSelecionados > 0 ? 'text-orange-800' : 'text-slate-600'}`}>{totais.qtdSelecionados}</p>
                <p className={`text-sm ${totais.qtdSelecionados > 0 ? 'text-orange-600' : 'text-slate-500'}`}>Selecionados</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="w-48">
              <Label className="text-xs">Banco</Label>
              <Select value={filtroBanco} onValueChange={setFiltroBanco}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os Bancos</SelectItem>
                  {bancosDisponiveis.map(banco => (
                    <SelectItem key={banco} value={banco}>{banco}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <Label className="text-xs">Correspondente</Label>
              <Select value={filtroCorrespondente} onValueChange={setFiltroCorrespondente}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  {correspondentesDisponiveis.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <Label className="text-xs">Status Pagamento</Label>
              <Select value={filtroStatus} onValueChange={setFiltroStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pendentes">Pendentes</SelectItem>
                  <SelectItem value="pagos">Pagos</SelectItem>
                  <SelectItem value="todos">Todos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {contratosSelecionados.length > 0 && (
              <Button 
                onClick={marcarSelecionadosComoPagos}
                className="gap-2 bg-emerald-600 hover:bg-emerald-700"
              >
                <CheckCircle2 className="h-4 w-4" />
                Marcar {contratosSelecionados.length} como Pago(s)
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabela de Contratos */}
      <Card>
        <CardHeader className="bg-gradient-to-r from-emerald-100 to-teal-100">
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-emerald-700" />
            Contratos para Vincular Comissão
          </CardTitle>
          <CardDescription>
            Selecione os contratos e registre as comissões recebidas
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-emerald-50">
                  <TableHead className="w-10 sticky left-0 bg-emerald-50 z-10">
                    <Checkbox 
                      checked={contratosSelecionados.length === contratosFiltrados.length && contratosFiltrados.length > 0}
                      onCheckedChange={toggleTodos}
                    />
                  </TableHead>
                  <TableHead className="font-bold text-xs">Data</TableHead>
                  <TableHead className="font-bold text-xs">Contrato</TableHead>
                  <TableHead className="font-bold text-xs">Cliente</TableHead>
                  <TableHead className="font-bold text-xs">Banco</TableHead>
                  <TableHead className="font-bold text-xs">Produto</TableHead>
                  <TableHead className="font-bold text-xs text-right">Valor Liberado</TableHead>
                  <TableHead className="font-bold text-xs text-center">Operador</TableHead>
                  <TableHead className="font-bold text-xs text-center bg-yellow-100">% Esperado</TableHead>
                  <TableHead className="font-bold text-xs text-right bg-yellow-100">R$ Esperado</TableHead>
                  <TableHead className="font-bold text-xs text-center bg-green-100">% Recebido</TableHead>
                  <TableHead className="font-bold text-xs text-right bg-green-100">R$ Recebido</TableHead>
                  {/* Colunas de distribuição com % e R$ */}
                  <TableHead className="font-bold text-xs text-center bg-amber-50">Corr %</TableHead>
                  <TableHead className="font-bold text-xs text-right bg-amber-100">Corr R$</TableHead>
                  <TableHead className="font-bold text-xs text-center bg-orange-50">Aten %</TableHead>
                  <TableHead className="font-bold text-xs text-right bg-orange-100">Aten R$</TableHead>
                  <TableHead className="font-bold text-xs text-center bg-purple-50">G.Ad %</TableHead>
                  <TableHead className="font-bold text-xs text-right bg-purple-100">G.Ad R$</TableHead>
                  <TableHead className="font-bold text-xs text-center bg-pink-50">G.Op %</TableHead>
                  <TableHead className="font-bold text-xs text-right bg-pink-100">G.Op R$</TableHead>
                  <TableHead className="font-bold text-xs text-center">Status</TableHead>
                  <TableHead className="font-bold text-xs text-center w-20">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contratosFiltrados.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={20} className="text-center py-8 text-slate-500">
                      Nenhum contrato encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  contratosFiltrados.map((c) => {
                    const emEdicao = modoEdicao[c.id];
                    const valores = valoresEditados[c.id] || {};
                    
                    // Calcular valores R$ baseados nos percentuais e comissão recebida
                    // Se em edição, usar valores editados; senão usar valores do contrato
                    const comissaoBase = emEdicao 
                      ? (parseFloat(valores.comissao_recebida_valor) || c.comissao_esperada || 0)
                      : (parseFloat(c.comissao_recebida_valor) || c.comissao_esperada || 0);
                    
                    const corrPerc = emEdicao 
                      ? (parseFloat(valores.corretor_percentual) || 0)
                      : (c.corretor_percentual ?? c.corretor_percentual_tabela ?? 30);
                    const atenPerc = emEdicao 
                      ? (parseFloat(valores.atendente_percentual) || 0)
                      : (c.atendente_percentual ?? c.atendente_percentual_tabela ?? 5);
                    const gadPerc = emEdicao 
                      ? (parseFloat(valores.ger_administrativo_percentual) || 0)
                      : (c.ger_administrativo_percentual ?? c.ger_adm_percentual_tabela ?? 10);
                    const gopPerc = emEdicao 
                      ? (parseFloat(valores.ger_operacional_percentual) || 0)
                      : (c.ger_operacional_percentual ?? c.ger_oper_percentual_tabela ?? 0);
                    
                    const corrValor = comissaoBase * (corrPerc / 100);
                    const atenValor = comissaoBase * (atenPerc / 100);
                    const gadValor = comissaoBase * (gadPerc / 100);
                    const gopValor = comissaoBase * (gopPerc / 100);
                    
                    return (
                      <TableRow key={c.id} className={c.comissao_paga ? 'bg-green-50' : 'hover:bg-slate-50'}>
                        <TableCell className="sticky left-0 bg-white z-10">
                          <Checkbox 
                            checked={contratosSelecionados.includes(c.id)}
                            onCheckedChange={() => toggleSelecao(c.id)}
                            disabled={c.comissao_paga}
                          />
                        </TableCell>
                        <TableCell className="text-xs">
                          {c.data_contrato ? new Date(c.data_contrato).toLocaleDateString("pt-BR") : "-"}
                        </TableCell>
                        <TableCell className="font-medium text-xs">
                          {c.numero_contrato || c.codigo || c.id?.substring(0, 8) || "-"}
                        </TableCell>
                        <TableCell className="text-xs max-w-[150px] truncate">
                          {c.cliente_nome || c.cliente || "-"}
                        </TableCell>
                        <TableCell className="text-xs">{c.banco || c.nome_banco || "-"}</TableCell>
                        <TableCell className="text-xs">{c.produto || c.tipo_operacao || "-"}</TableCell>
                        <TableCell className="text-right text-xs font-medium">
                          {formatCurrency(c.valor_liberado_num)}
                        </TableCell>
                        <TableCell className="text-center text-xs">
                          {c.operador_comissao || c.vendedor || "-"}
                        </TableCell>
                        <TableCell className="text-center text-xs bg-yellow-50 font-medium">
                          {c.percentual_esperado.toFixed(2)}%
                        </TableCell>
                        <TableCell className="text-right text-xs bg-yellow-50 font-medium">
                          {formatCurrency(c.comissao_esperada)}
                        </TableCell>
                        <TableCell className="text-center text-xs bg-green-50">
                          {emEdicao ? (
                            <Input 
                              type="number"
                              step="0.01"
                              value={valores.comissao_recebida_percentual || ""}
                              onChange={e => atualizarValorEditado(c.id, "comissao_recebida_percentual", e.target.value)}
                              onBlur={() => calcularValorDePercentual(c.id, "comissao_recebida")}
                              className="h-7 w-16 text-xs text-center"
                              placeholder="%"
                            />
                          ) : (
                            c.comissao_recebida_percentual ? `${parseFloat(c.comissao_recebida_percentual).toFixed(2)}%` : "-"
                          )}
                        </TableCell>
                        <TableCell className="text-right text-xs bg-green-50">
                          {emEdicao ? (
                            <Input 
                              type="number"
                              step="0.01"
                              value={valores.comissao_recebida_valor || ""}
                              onChange={e => atualizarValorEditado(c.id, "comissao_recebida_valor", e.target.value)}
                              onBlur={() => calcularPercentualDeValor(c.id, "comissao_recebida")}
                              className="h-7 w-20 text-xs text-right"
                              placeholder="R$"
                            />
                          ) : (
                            c.comissao_recebida_valor ? formatCurrency(c.comissao_recebida_valor) : "-"
                          )}
                        </TableCell>
                        {/* Colunas de distribuição com % e R$ */}
                        {/* Corretor */}
                        <TableCell className="text-center text-xs bg-amber-50">
                          {emEdicao ? (
                            <Input 
                              type="number"
                              step="0.01"
                              value={valores.corretor_percentual !== undefined && valores.corretor_percentual !== "" ? valores.corretor_percentual : ""}
                              onChange={e => atualizarValorEditado(c.id, "corretor_percentual", e.target.value)}
                              className="h-7 w-14 text-xs text-center"
                              placeholder="30"
                            />
                          ) : (
                            c.corretor_percentual !== undefined && c.corretor_percentual !== null ? `${parseFloat(c.corretor_percentual).toFixed(0)}` : 
                            c.corretor_percentual_tabela !== undefined ? `${c.corretor_percentual_tabela.toFixed(0)}` : "30"
                          )}
                        </TableCell>
                        <TableCell className="text-right text-xs bg-amber-100 font-medium">
                          {formatCurrency(corrValor)}
                        </TableCell>
                        {/* Atendente */}
                        <TableCell className="text-center text-xs bg-orange-50">
                          {emEdicao ? (
                            <Input 
                              type="number"
                              step="0.01"
                              value={valores.atendente_percentual !== undefined && valores.atendente_percentual !== "" ? valores.atendente_percentual : ""}
                              onChange={e => atualizarValorEditado(c.id, "atendente_percentual", e.target.value)}
                              className="h-7 w-14 text-xs text-center"
                              placeholder="5"
                            />
                          ) : (
                            c.atendente_percentual !== undefined && c.atendente_percentual !== null ? `${parseFloat(c.atendente_percentual).toFixed(0)}` : 
                            c.atendente_percentual_tabela !== undefined ? `${c.atendente_percentual_tabela.toFixed(0)}` : "5"
                          )}
                        </TableCell>
                        <TableCell className="text-right text-xs bg-orange-100 font-medium">
                          {formatCurrency(atenValor)}
                        </TableCell>
                        {/* Gerente Administrativo */}
                        <TableCell className="text-center text-xs bg-purple-50">
                          {emEdicao ? (
                            <Input 
                              type="number"
                              step="0.01"
                              value={valores.ger_administrativo_percentual !== undefined && valores.ger_administrativo_percentual !== "" ? valores.ger_administrativo_percentual : ""}
                              onChange={e => atualizarValorEditado(c.id, "ger_administrativo_percentual", e.target.value)}
                              className="h-7 w-14 text-xs text-center"
                              placeholder="10"
                            />
                          ) : (
                            c.ger_administrativo_percentual !== undefined && c.ger_administrativo_percentual !== null ? `${parseFloat(c.ger_administrativo_percentual).toFixed(0)}` : 
                            c.ger_adm_percentual_tabela !== undefined ? `${c.ger_adm_percentual_tabela.toFixed(0)}` : "10"
                          )}
                        </TableCell>
                        <TableCell className="text-right text-xs bg-purple-100 font-medium">
                          {formatCurrency(gadValor)}
                        </TableCell>
                        {/* Gerente Operacional */}
                        <TableCell className="text-center text-xs bg-pink-50">
                          {emEdicao ? (
                            <Input 
                              type="number"
                              step="0.01"
                              value={valores.ger_operacional_percentual !== undefined && valores.ger_operacional_percentual !== "" ? valores.ger_operacional_percentual : ""}
                              onChange={e => atualizarValorEditado(c.id, "ger_operacional_percentual", e.target.value)}
                              className="h-7 w-14 text-xs text-center"
                              placeholder="0"
                            />
                          ) : (
                            c.ger_operacional_percentual !== undefined && c.ger_operacional_percentual !== null ? `${parseFloat(c.ger_operacional_percentual).toFixed(0)}` : 
                            c.ger_oper_percentual_tabela !== undefined ? `${c.ger_oper_percentual_tabela.toFixed(0)}` : "0"
                          )}
                        </TableCell>
                        <TableCell className="text-right text-xs bg-pink-100 font-medium">
                          {formatCurrency(gopValor)}
                        </TableCell>
                        <TableCell className="text-center">
                          {c.comissao_paga ? (
                            <Badge className="bg-green-600 text-xs">PAGO</Badge>
                          ) : (
                            <Badge variant="outline" className="text-orange-600 border-orange-300 text-xs">PENDENTE</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {emEdicao ? (
                            <div className="flex gap-1 justify-center">
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="h-7 w-7 p-0 text-green-600"
                                onClick={() => salvarComissao(c.id)}
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="h-7 w-7 p-0 text-red-600"
                                onClick={() => cancelarEdicao(c.id)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-7 w-7 p-0 text-blue-600"
                              onClick={() => iniciarEdicao(c)}
                              disabled={c.comissao_paga}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Rodapé com Total */}
      <Card className="bg-emerald-800 text-white">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <DollarSign className="h-8 w-8" />
              <div>
                <p className="text-sm opacity-80">TOTAL COMISSÃO</p>
                <p className="text-3xl font-bold">{formatCurrency(totais.comissaoTotal)}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm opacity-80">{totais.qtdTotal} contratos</p>
              <p className="text-lg">Valor: {formatCurrency(totais.valorTotal)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dialog de Importação */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Importar Pagamentos de Comissão
            </DialogTitle>
            <DialogDescription>
              Importe uma planilha com os pagamentos recebidos do banco
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {!arquivoImportado ? (
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
                <Upload className="h-12 w-12 mx-auto text-slate-400 mb-4" />
                <p className="text-slate-600 mb-4">Arraste um arquivo ou clique para selecionar</p>
                <input 
                  type="file" 
                  accept=".xlsx,.xls,.csv" 
                  onChange={importarArquivo}
                  className="hidden"
                  id="import-file"
                />
                <label htmlFor="import-file">
                  <Button asChild variant="outline">
                    <span>Selecionar Arquivo</span>
                  </Button>
                </label>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <p className="font-medium text-green-800">Arquivo: {arquivoImportado.name}</p>
                  <p className="text-sm text-green-600">{dadosImportados.length} registros encontrados</p>
                </div>
                
                {dadosImportados.length > 0 && (
                  <div className="max-h-60 overflow-auto border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          {Object.keys(dadosImportados[0]).slice(0, 5).map(key => (
                            <TableHead key={key} className="text-xs">{key}</TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {dadosImportados.slice(0, 5).map((row, idx) => (
                          <TableRow key={idx}>
                            {Object.values(row).slice(0, 5).map((val, vIdx) => (
                              <TableCell key={vIdx} className="text-xs">{String(val)}</TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}
            
            <p className="text-xs text-slate-500">
              O arquivo deve conter: Número do Contrato, Comissão Recebida (valor), Data Pagamento
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowImportDialog(false);
              setArquivoImportado(null);
              setDadosImportados([]);
            }}>
              Cancelar
            </Button>
            {dadosImportados.length > 0 && (
              <Button onClick={vincularDadosImportados} className="bg-emerald-600 hover:bg-emerald-700">
                Vincular Contratos
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
